import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FormEvent, PointerEvent as ReactPointerEvent } from "react";
import "flag-icons/css/flag-icons.min.css";
import "../../../../App.css";
import "./CheckinCheckoutApp.css";
import { API_BASE_URL, FALLBACK_LANGUAGE } from "../../../../config";
import { getAuthHeaders } from "../../../../api/auth";
import { fetchPrimaryLoadingLocations, searchSecondaryLoadingLocations, type LoadingLocation } from "../../../../api/loadingLocations";
import { fetchLIFNRByContractNumber } from "../../../../api/contracts";
import PfleidererLogo from "../../shared/components/PfleidererLogo.svg";
import { AssortmentDistributionView } from "../../../../components/AssortmentDistributionView";
import {
  SUPPORTED_LANGUAGES,
  getTranslations,
  resolveLanguageCandidate,
  type LanguageKey,
  type TranslationKey
} from "./translations";

// Hide AIpad footer when this component is mounted
function useHideAIpadFooter() {
  useEffect(() => {
    // If running in iframe (Feed container), target parent window body
    const targetBody = window.self !== window.top && window.parent
      ? window.parent.document.body
      : document.body;

    targetBody.classList.add('hide-aipad-footer');

    return () => {
      targetBody.classList.remove('hide-aipad-footer');
    };
  }, []);
}

// TenantId wird jetzt aus dem JWT Token extrahiert (Backend holt es automatisch)

interface BatchTransportInfo {
  id: string;
  transportBatchNumber: string; // BatchTransport still uses old field name
  yardDocumentNumber?: string; // Generated LKW_LOAD_ID (TRANSPORTCHARGE + suffix)
  truckLicensePlate: string | null; // Will map to KENNZEICHEN
  carrierName: string | null; // Will map to SPEDITION
  supplierReferenceNumber: string | null; // Will map to LIEFERANTENBELEGNR
  fraction: string | null; // Will map to FRAKTION
  woodGroup: string | null; // Will map to HOLZGRUPPE
  assortment: string | null; // Will map to SORTIMENT
  wasteKey: string | null;
  loadingLocation: string | null; // Ladeort (nicht Abfallort!)
  destinationLocation: string | null;
  Contract: string | null; // VERTRAG - needed to decide if assortment distribution is required
  quantityTrucks: number | null; // Geplante Anzahl LKWs für diese Charge
  requiresWasteSelection: boolean; // true wenn Werk 0128
}

type Stage = "welcome" | "language" | "mode" | "checkin" | "checkout" | "redirectToGuard";
type RedirectReason = "precheckin-required";
type CheckinStep =
  | "batchNumber"
  | "licensePlate"
  | "supplierDocument"
  | "assortmentDistribution"
  | "wasteDetails"
  | "weightWaiting"
  | "weightResult"
  | "success";

type CheckoutStep =
  | "batchNumber"
  | "licensePlate"
  | "weightWaiting"
  | "weightResult"
  | "signature"
  | "success";

const WEIGHT_DELAY_MS = 5000;
const WEIGHT_CONFIRMATION_POLL_INTERVAL_MS = 2000;

type WeightConfirmationPhase = "idle" | "creating" | "pending" | "confirmed" | "error";

interface WeightConfirmationState {
  id: string | null;
  phase: WeightConfirmationPhase;
  error: string | null;
}

interface WeightConfirmationPayload {
  transportBatchNumber: string;
  direction: "checkin" | "checkout";
  weight: number;
  licensePlate: string | null;
  batchTransportId: string | null;
}

interface WeightConfirmationResponse {
  id: string;
  status: "pending" | "confirmed" | "superseded";
  transportBatchNumber: string;
  direction: "checkin" | "checkout";
  weight: number;
  licensePlate: string | null;
  createdAt: string;
  confirmedAt: string | null;
  confirmedBy: string | null;
  batchTransportId: string | null;
}
const RESET_DURATION_SECONDS = 20;

function generateRandomWeight(): number {
  const min = 18000;
  const max = 42000;
  return Math.floor(min + Math.random() * (max - min));
}

function formatWeight(language: string, value: number | null): string {
  if (value == null) {
    return "--";
  }

  return new Intl.NumberFormat(language, {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0
  }).format(value);
}

// Neue Funktion: Lade ALLE BatchTransports für eine Transportnummer im richtigen Status
async function loadAllBatchTransports(
  batchNumber: string,
  language: string
): Promise<BatchTransportInfo[]> {
  const normalized = batchNumber.trim();

  // Suche nach allen batchTransports mit dieser Nummer
  const batchUrl = `${API_BASE_URL}/api/batch-transports?transportBatchNumber=${encodeURIComponent(normalized)}`;
  const batchResponse = await fetch(batchUrl, {
    headers: {
      ...getAuthHeaders(),
      "Accept-Language": language
    },
    credentials: "include"
  });

  if (!batchResponse.ok) {
    const error = new Error(batchResponse.status === 404 ? "not-found" : "lookup-failed");
    error.name = batchResponse.status === 404 ? "NotFound" : "LookupFailed";
    throw error;
  }

  const batchPayload = await batchResponse.json();
  if (!Array.isArray(batchPayload) || batchPayload.length === 0) {
    const error = new Error("not-found");
    error.name = "NotFound";
    throw error;
  }

  // Load destinationLocation from ZHBT48GT (SAP) - nur einmal für alle
  let destinationLocation: string | null = null;
  try {
    const validationUrl = `${API_BASE_URL}/api/batch-transports/validate-precheckin/${encodeURIComponent(normalized)}`;
    const validationResponse = await fetch(validationUrl, {
      headers: {
        ...getAuthHeaders(),
        "Accept-Language": language
      },
      credentials: "include"
    });

    if (validationResponse.ok) {
      const validationData = await validationResponse.json();
      destinationLocation = validationData.destinationLocation ?? null;
    }
  } catch (error) {
    console.warn("[loadAllBatchTransports] Failed to load destinationLocation from ZHBT48GT:", error);
  }

  // Konvertiere alle gefundenen batchTransports
  return batchPayload.map((batchTransport: any) => ({
    id: batchTransport._id,
    transportBatchNumber: batchTransport.transportBatchNumber,
    truckLicensePlate: batchTransport.truckLicensePlate ?? null,
    carrierName: batchTransport.carrierName ?? batchTransport.SupplierName ?? null,
    supplierReferenceNumber: batchTransport.supplierReferenceNumber ?? null,
    fraction: batchTransport.fraction ?? batchTransport.Fraction ?? null,
    woodGroup: batchTransport.woodGroup ?? null,
    assortment: batchTransport.assortment ?? batchTransport.Sortimentbez ?? null,
    wasteKey: batchTransport.ABFALLSCHL ?? null,
    loadingLocation: batchTransport.LADEORT ?? null,
    destinationLocation,
    Contract: batchTransport.Contract ?? null,
    quantityTrucks: batchTransport.quantityTrucks ?? null,
    requiresWasteSelection: batchTransport.Plant === "0128"
  }));
}

async function loadBatchTransport(
  batchNumber: string,
  language: string
): Promise<BatchTransportInfo> {
  const normalized = batchNumber.trim();

  // Suche nach batchTransport
  const batchUrl = `${API_BASE_URL}/api/batch-transports?transportBatchNumber=${encodeURIComponent(normalized)}`;
  const batchResponse = await fetch(batchUrl, {
    headers: {
      ...getAuthHeaders(),
      "Accept-Language": language
    },
    credentials: "include"
  });

  if (!batchResponse.ok) {
    const error = new Error(batchResponse.status === 404 ? "not-found" : "lookup-failed");
    error.name = batchResponse.status === 404 ? "NotFound" : "LookupFailed";
    throw error;
  }

  const batchPayload = await batchResponse.json();
  if (!Array.isArray(batchPayload) || batchPayload.length === 0) {
    const error = new Error("not-found");
    error.name = "NotFound";
    throw error;
  }

  // Erster gefundener batchTransport
  const batchTransport = batchPayload[0];

  // Load destinationLocation from ZHBT48GT (SAP)
  let destinationLocation: string | null = null;
  try {
    const validationUrl = `${API_BASE_URL}/api/batch-transports/validate-precheckin/${encodeURIComponent(normalized)}`;
    const validationResponse = await fetch(validationUrl, {
      headers: {
        ...getAuthHeaders(),
        "Accept-Language": language
      },
      credentials: "include"
    });

    if (validationResponse.ok) {
      const validationData = await validationResponse.json();
      destinationLocation = validationData.destinationLocation ?? null;
    }
  } catch (error) {
    console.warn("[loadBatchTransport] Failed to load destinationLocation from ZHBT48GT:", error);
  }

  return {
    id: batchTransport._id,
    transportBatchNumber: batchTransport.transportBatchNumber,
    truckLicensePlate: batchTransport.truckLicensePlate ?? null,
    carrierName: batchTransport.carrierName ?? batchTransport.SupplierName ?? null,
    supplierReferenceNumber: batchTransport.supplierReferenceNumber ?? null,
    fraction: batchTransport.fraction ?? batchTransport.Fraction ?? null,
    woodGroup: batchTransport.woodGroup ?? null,
    assortment: batchTransport.assortment ?? batchTransport.Sortimentbez ?? null,
    wasteKey: batchTransport.ABFALLSCHL ?? null,
    loadingLocation: batchTransport.LADEORT ?? null,
    destinationLocation,
    Contract: batchTransport.Contract ?? null,
    quantityTrucks: batchTransport.quantityTrucks ?? null,
    requiresWasteSelection: batchTransport.Plant === "0128"
  };
}

async function createOrUpdateYardDocument(
  batchTransportNumber: string,
  batchTransportId: string,
  truckLicensePlate: string,
  language: string,
  status: string,
  supplierDocumentNumber?: string,
  wasteKey?: string,
  loadingLocation?: string
): Promise<{ id: string; transportBatchNumber: string; existingLicensePlate?: string }> {
  // Search for existing yard documents with same TRANSPORTCHARGE
  const searchUrl = `${API_BASE_URL}/api/yard-documents?filter[TRANSPORTCHARGE]=${encodeURIComponent(batchTransportNumber)}`;

  const searchResponse = await fetch(searchUrl, {
    headers: {
      ...getAuthHeaders(),
      "Accept-Language": language
    },
    credentials: "include"
  });

  if (!searchResponse.ok) {
    console.error("[CheckinCheckout] Failed to search yard documents:", searchResponse.status);
    throw new Error(`Fehler beim Suchen der Yard-Documents: ${searchResponse.status}`);
  }

  const existingDocs = await searchResponse.json();
  const relatedDocuments = Array.isArray(existingDocs) ? existingDocs : [];

  console.log("[createOrUpdateYardDocument] Found existing documents:", {
    count: relatedDocuments.length,
    documents: relatedDocuments.map((d: any) => ({
      id: d._id,
      lkwLoadId: d.LKW_LOAD_ID,
      status: d.LKW_LOAD_STATUS,
      kennzeichen: d.KENNZEICHEN
    }))
  });

  // Check if there's already a document from PreCheckin (LKW_LOAD_STATUS = "1")
  const preCheckinDoc = relatedDocuments.find((doc: any) =>
    doc.LKW_LOAD_STATUS === "1" || doc.status === 1
  );

  if (preCheckinDoc) {
    // UPDATE existing PreCheckin document
    console.log("[CheckinCheckout] Found existing PreCheckin document:", preCheckinDoc._id);

    const updateUrl = `${API_BASE_URL}/api/yard-documents/${preCheckinDoc._id}`;
    const updatePayload: Record<string, unknown> = {
      LKW_LOAD_STATUS: status,
    };

    // Update license plate if provided and different
    if (truckLicensePlate && truckLicensePlate !== preCheckinDoc.KENNZEICHEN) {
      updatePayload.KENNZEICHEN = truckLicensePlate;
    }

    // Update supplier document number if provided
    if (supplierDocumentNumber) {
      updatePayload.LIEFERANTENBELEGNR = supplierDocumentNumber;
    }

    const updateResponse = await fetch(updateUrl, {
      method: "PUT",
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
        "Accept-Language": language
      },
      credentials: "include",
      body: JSON.stringify(updatePayload)
    });

    if (!updateResponse.ok) {
      console.error("[CheckinCheckout] Failed to update yard document:", updateResponse.status);
      throw new Error(`Fehler beim Aktualisieren des Yard-Documents: ${updateResponse.status}`);
    }

    const updatedDocument = await updateResponse.json();
    return {
      id: updatedDocument._id,
      transportBatchNumber: preCheckinDoc.LKW_LOAD_ID || preCheckinDoc.TRANSPORTCHARGE,
      existingLicensePlate: preCheckinDoc.KENNZEICHEN || undefined
    };
  }

  // No PreCheckin document found - CREATE new one
  console.log("[createOrUpdateYardDocument] No PreCheckin document found, creating new one");

  // Find highest LKW_LOAD_ID suffix for this TRANSPORTCHARGE
  let maxSuffix = 0;
  for (const doc of relatedDocuments) {
    const lkwLoadId = doc.LKW_LOAD_ID || "";
    // Extract suffix (last 3 digits) from LKW_LOAD_ID
    const suffixMatch = lkwLoadId.match(/(\d{3})$/);
    if (suffixMatch) {
      const suffix = parseInt(suffixMatch[1], 10);
      if (!isNaN(suffix) && suffix > maxSuffix) {
        maxSuffix = suffix;
      }
    }
  }

  // Generate next LKW_LOAD_ID: TRANSPORTCHARGE + suffix (001, 002, 003...)
  const nextSuffix = maxSuffix + 1;
  console.log("[createOrUpdateYardDocument] Calculated next suffix:", {
    maxSuffix,
    nextSuffix,
    nextLkwLoadId: batchTransportNumber + String(nextSuffix).padStart(3, '0')
  });
  const newLkwLoadId = `${batchTransportNumber}${String(nextSuffix).padStart(3, "0")}`;

  // Load batchTransport data using transportBatchNumber (not MongoDB _id!)
  const batchUrl = `${API_BASE_URL}/api/batch-transports?transportBatchNumber=${encodeURIComponent(batchTransportNumber)}`;
  const batchResponse = await fetch(batchUrl, {
    headers: {
      ...getAuthHeaders(),
      "Accept-Language": language
    },
    credentials: "include"
  });

  if (!batchResponse.ok) {
    console.error("[CheckinCheckout] Failed to load batch transport:", batchResponse.status);
    throw new Error(`Fehler beim Laden des Batch-Transports: ${batchResponse.status}`);
  }

  const batchTransportArray = await batchResponse.json();
  const batchTransport = Array.isArray(batchTransportArray) && batchTransportArray.length > 0
    ? batchTransportArray[0]
    : null;

  if (!batchTransport) {
    throw new Error(`Batch-Transport ${batchTransportNumber} nicht gefunden`);
  }

  // Create new YardDocument with SAP field names
  const createUrl = `${API_BASE_URL}/api/yard-documents`;
  const createResponse = await fetch(createUrl, {
    method: "POST",
    headers: {
      ...getAuthHeaders(),
      "Content-Type": "application/json",
      "Accept-Language": language
    },
    credentials: "include",
    body: JSON.stringify({
      type: "yardDocument",
      TRANSPORTCHARGE: batchTransportNumber,
      LKW_LOAD_ID: newLkwLoadId,
      KENNZEICHEN: truckLicensePlate,
      LIEFERANTENBELEGNR: supplierDocumentNumber || "",
      FAHRERNAME: batchTransport.driverName || "",
      WERK2: batchTransport.Plant || "",
      SPEDITION: batchTransport.carrierName || batchTransport.SupplierName || "",
      LIEFERANT: batchTransport.SupplierName || "",
      FRAKTION: batchTransport.fraction || batchTransport.Fraction || "",
      HOLZGRUPPE: batchTransport.woodGroup || "",
      SORTIMENT: batchTransport.assortment || batchTransport.Sortimentbez || "",
      VERTRAG: batchTransport.Contract || "",
      VERTPOS: batchTransport.ContractPos || "",
      KALENDERWOCHE: batchTransport.CalWeek || "",
      ...(wasteKey ? { ABFALLSCHL: wasteKey } : {}),
      ...(loadingLocation ? { LADEORT: loadingLocation } : {}),
      LKW_LOAD_STATUS: status,
      driverLanguage: language
    })
  });

  if (!createResponse.ok) {
    console.error("[CheckinCheckout] Failed to create yard document:", createResponse.status);
    throw new Error(`Fehler beim Erstellen des Yard-Documents: ${createResponse.status}`);
  }

  const createdDocument = await createResponse.json();
  return {
    id: createdDocument._id,
    transportBatchNumber: newLkwLoadId
  };
}

export function CheckinCheckoutApp() {
  // Hide AIpad footer (Chat button)
  useHideAIpadFooter();

  const [selectedLanguage, setSelectedLanguage] = useState<LanguageKey>(() =>
    resolveLanguageCandidate(FALLBACK_LANGUAGE)
  );
  const translations = useMemo(() => getTranslations(selectedLanguage), [selectedLanguage]);
  const t = useCallback(
    (key: TranslationKey) => translations[key] ?? key,
    [translations]
  );

  const [stage, setStage] = useState<Stage>("welcome");
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [redirectReason, setRedirectReason] = useState<RedirectReason | null>(null);
  const [redirectCountdown, setRedirectCountdown] = useState(15);

  const [checkinStep, setCheckinStep] = useState<CheckinStep>("batchNumber");
  const [checkinBatchNumber, setCheckinBatchNumber] = useState("");
  const [checkinBatchError, setCheckinBatchError] = useState<string | null>(null);
  const [checkinTransport, setCheckinTransport] = useState<BatchTransportInfo | null>(null);
  const [checkinAvailableBatches, setCheckinAvailableBatches] = useState<BatchTransportInfo[]>([]);
  const [checkinLicensePlate, setCheckinLicensePlate] = useState("");
  const [checkinLicenseError, setCheckinLicenseError] = useState<string | null>(null);
  const [isLicensePlateFromPreCheckin, setIsLicensePlateFromPreCheckin] = useState(false);
  const [supplierDocumentNumber, setSupplierDocumentNumber] = useState("");
  const [supplierDocumentError, setSupplierDocumentError] = useState<string | null>(null);
  const [wasteKey, setWasteKey] = useState("");
  const [loadingLocation, setLoadingLocation] = useState("");
  const [wasteError, setWasteError] = useState<string | null>(null);

  // Loading Locations ValueHelp
  const [lifnr, setLifnr] = useState<string | null>(null);
  const [primaryLocations, setPrimaryLocations] = useState<LoadingLocation[]>([]);
  const [secondaryLocations, setSecondaryLocations] = useState<LoadingLocation[]>([]);
  const [loadingPrimary, setLoadingPrimary] = useState(false);
  const [loadingSecondary, setLoadingSecondary] = useState(false);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [locationSearchTerm, setLocationSearchTerm] = useState("");
  const locationDropdownRef = useRef<HTMLDivElement>(null);
  const locationInputRef = useRef<HTMLInputElement>(null);
  const [checkinWeight, setCheckinWeight] = useState<number | null>(null);

  const [checkoutStep, setCheckoutStep] = useState<CheckoutStep>("batchNumber");
  const [checkoutBatchNumber, setCheckoutBatchNumber] = useState("");
  const [checkoutBatchError, setCheckoutBatchError] = useState<string | null>(null);
  const [checkoutLicensePlate, setCheckoutLicensePlate] = useState("");
  const [checkoutLicenseError, setCheckoutLicenseError] = useState<string | null>(null);
  const [checkoutTransport, setCheckoutTransport] = useState<BatchTransportInfo | null>(null);
  const [checkoutAvailableBatches, setCheckoutAvailableBatches] = useState<BatchTransportInfo[]>([]);
  const [checkoutWeight, setCheckoutWeight] = useState<number | null>(null);
  const [signatureError, setSignatureError] = useState<string | null>(null);

  const [loadingTransport, setLoadingTransport] = useState(false);

  const [autoResetSecondsRemaining, setAutoResetSecondsRemaining] = useState<number | null>(null);

  const [checkinWeightConfirmation, setCheckinWeightConfirmation] = useState<WeightConfirmationState>(
    () => ({ id: null, phase: "idle", error: null })
  );
  const [checkoutWeightConfirmation, setCheckoutWeightConfirmation] = useState<WeightConfirmationState>(
    () => ({ id: null, phase: "idle", error: null })
  );

  const signatureCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const isDrawingRef = useRef(false);
  const hasStrokeRef = useRef(false);
  const checkinWeightConfirmationPollRef = useRef<number | null>(null);
  const checkoutWeightConfirmationPollRef = useRef<number | null>(null);
  const [hasSignature, setHasSignature] = useState(false);

  const resetFlows = useCallback(() => {
    setStage("mode");
    setCheckinStep("batchNumber");
    setCheckinBatchNumber("");
    setCheckinBatchError(null);
    setCheckinTransport(null);
    setCheckinAvailableBatches([]);
    setCheckinLicensePlate("");
    setCheckinLicenseError(null);
    setIsLicensePlateFromPreCheckin(false);
    setWasteKey("");
    setLoadingLocation("");
    setWasteError(null);
    setCheckinWeight(null);
    setCheckinWeightConfirmation({ id: null, phase: "idle", error: null });

    setCheckoutStep("batchNumber");
    setCheckoutBatchNumber("");
    setCheckoutBatchError(null);
    setCheckoutLicensePlate("");
    setCheckoutLicenseError(null);
    setCheckoutTransport(null);
    setCheckoutAvailableBatches([]);
    setCheckoutWeight(null);
    setSignatureError(null);
    setHasSignature(false);
    setCheckoutWeightConfirmation({ id: null, phase: "idle", error: null });

    if (checkinWeightConfirmationPollRef.current) {
      window.clearInterval(checkinWeightConfirmationPollRef.current);
      checkinWeightConfirmationPollRef.current = null;
    }

    if (checkoutWeightConfirmationPollRef.current) {
      window.clearInterval(checkoutWeightConfirmationPollRef.current);
      checkoutWeightConfirmationPollRef.current = null;
    }
    hasStrokeRef.current = false;
  }, []);

  const goToModeSelection = useCallback(() => {
    setStage("mode");
    setCheckinStep("batchNumber");
    setCheckoutStep("batchNumber");
    setCheckinTransport(null);
    setCheckinBatchNumber("");
    setCheckoutBatchNumber("");
    setCheckinLicensePlate("");
    setIsLicensePlateFromPreCheckin(false);
    setCheckoutLicensePlate("");
    setCheckoutTransport(null);
    setCheckinWeightConfirmation({ id: null, phase: "idle", error: null });
    setCheckoutWeightConfirmation({ id: null, phase: "idle", error: null });
  }, []);

  const handleLanguageSelect = useCallback((language: LanguageKey) => {
    setSelectedLanguage(language);
    setShowLanguageMenu(false);
    // After language selection, go to mode selection (Checkin/Checkout choice)
    if (stage === "language") {
      setStage("mode");
    }
  }, [stage]);

  // Load primary loading locations when LIFNR is available
  useEffect(() => {
    if (!lifnr) {
      setPrimaryLocations([]);
      return;
    }

    const loadPrimaryLocations = async () => {
      setLoadingPrimary(true);
      try {
        const locations = await fetchPrimaryLoadingLocations(lifnr);
        setPrimaryLocations(locations);

        // Auto-select if exactly 1 location returned and field is empty
        if (locations.length === 1 && !loadingLocation) {
          const location = locations[0];
          setLoadingLocation(location.displayText);
        }
      } catch (error) {
        console.error("Failed to load primary loading locations:", error);
        setPrimaryLocations([]);
      } finally {
        setLoadingPrimary(false);
      }
    };

    void loadPrimaryLocations();
  }, [lifnr, loadingLocation]);

  // Search secondary locations when user types
  useEffect(() => {
    if (!locationSearchTerm || locationSearchTerm.length < 2) {
      setSecondaryLocations([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setLoadingSecondary(true);
      try {
        const locations = await searchSecondaryLoadingLocations(locationSearchTerm, 10);
        setSecondaryLocations(locations);
      } catch (error) {
        console.error("Failed to search loading locations:", error);
        setSecondaryLocations([]);
      } finally {
        setLoadingSecondary(false);
      }
    }, 300); // Debounce 300ms

    return () => clearTimeout(timeoutId);
  }, [locationSearchTerm]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        locationDropdownRef.current &&
        !locationDropdownRef.current.contains(event.target as Node) &&
        locationInputRef.current &&
        !locationInputRef.current.contains(event.target as Node)
      ) {
        setShowLocationDropdown(false);
      }
    };

    if (showLocationDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showLocationDropdown]);

  const handleCheckinBatchSubmit = useCallback(async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = checkinBatchNumber.trim();

    if (!trimmed) {
      setCheckinBatchError(t("validation.required"));
      return;
    }

    setLoadingTransport(true);
    setCheckinBatchError(null);

    try {
      // === Input Detection: LKW_LOAD_ID (16 Stellen) vs TRANSPORTCHARGE (13 Stellen) ===
      let transportChargeToUse = trimmed;
      let isLkwLoadId = false;

      // Wenn Input ≥ 16 Stellen: vermutlich LKW_LOAD_ID (Format: TRANSPORTCHARGE + 3-stellige Nummer)
      // Beispiel: "1000034027001001" → TRANSPORTCHARGE = "1000034027001", Suffix = "001"
      if (trimmed.length >= 16) {
        // Extrahiere TRANSPORTCHARGE direkt (erste 13 Stellen)
        // LKW_LOAD_ID Format: TRANSPORTCHARGE (13) + laufende Nummer (3)
        transportChargeToUse = trimmed.substring(0, 13);
        isLkwLoadId = true;
        console.log("[CheckinCheckout] Input detected as LKW_LOAD_ID (≥16 chars), extracted TRANSPORTCHARGE:", transportChargeToUse);
      }

      console.log("[CheckinCheckout] Using TRANSPORTCHARGE for lookup:", { originalInput: trimmed, transportCharge: transportChargeToUse, isLkwLoadId });

      // Lade ALLE BatchTransports mit dieser TRANSPORTCHARGE
      const allBatches = await loadAllBatchTransports(transportChargeToUse, selectedLanguage);

      // Speichere alle gefundenen Batches
      setCheckinAvailableBatches(allBatches);

      // Wenn nur 1 Batch gefunden: direkt verwenden
      // Wenn 2+ Batches gefunden: Auswahl anhand Kennzeichen
      if (allBatches.length === 1) {
        const transport = allBatches[0];
        setCheckinTransport(transport);
        setCheckinLicensePlate(transport.truckLicensePlate ?? "");
        setSupplierDocumentNumber(transport.supplierReferenceNumber ?? "");
        setWasteKey(transport.wasteKey ?? "");
        setLoadingLocation(transport.loadingLocation ?? "");

        // Load LIFNR from Contract for loading locations
        if (transport.Contract) {
          try {
            const contractLifnr = await fetchLIFNRByContractNumber(transport.Contract);
            setLifnr(contractLifnr);
          } catch (error) {
            console.error("Failed to load LIFNR from contract:", error);
            setLifnr(null);
          }
        } else {
          setLifnr(null);
        }

        // Check if yardDocument already exists
        const searchUrl = `${API_BASE_URL}/api/yard-documents?filter[TRANSPORTCHARGE]=${encodeURIComponent(transportChargeToUse)}`;
        const searchResponse = await fetch(searchUrl, {
          headers: {
            ...getAuthHeaders(),
            "Accept-Language": selectedLanguage
          },
          credentials: "include"
        });

        let existingYardDoc = null;
        let existingDocs: any[] = [];
        if (searchResponse.ok) {
          const docs = await searchResponse.json();
          console.log("[CheckinCheckout] Yard documents search result:", { count: Array.isArray(docs) ? docs.length : 0, docs });
          if (Array.isArray(docs)) {
            existingDocs = docs;
            if (docs.length > 0) {
              existingYardDoc = docs[0];
              console.log("[CheckinCheckout] Found existing yard document:", existingYardDoc);
            } else {
              console.log("[CheckinCheckout] No yard documents found for TRANSPORTCHARGE:", transportChargeToUse);
            }
          }
        } else {
          console.error("[CheckinCheckout] Yard documents search failed:", searchResponse.status, searchResponse.statusText);
        }

        // Check if quantityTrucks limit is reached
        if (transport.quantityTrucks && existingDocs.length >= transport.quantityTrucks) {
          setRedirectReason("precheckin-required");
          setRedirectCountdown(15);
          setStage("redirectToGuard");
          setLoadingTransport(false);
          return;
        }

        // If NO yardDocument exists, validate PreCheckin requirement
        if (!existingYardDoc) {
          try {
            const validationUrl = `${API_BASE_URL}/api/batch-transports/validate-precheckin/${encodeURIComponent(transportChargeToUse)}`;
            const validationResponse = await fetch(validationUrl, {
              headers: {
                ...getAuthHeaders(),
                "Accept-Language": selectedLanguage
              },
              credentials: "include"
            });

            if (validationResponse.ok) {
              const { preCheckinAllowed } = await validationResponse.json();

              // If PreCheckin is REQUIRED (PRECHECKIN = 'X'), redirect to guard
              if (preCheckinAllowed) {
                setRedirectReason("precheckin-required");
                setRedirectCountdown(15);
                setStage("redirectToGuard");
                setLoadingTransport(false);
                return;
              }
              // If PreCheckin is NOT required, continue normal flow
            }
          } catch (validationError) {
            console.error("[CheckinCheckout] PreCheckin validation failed:", validationError);
            // Continue anyway on validation error (fail-open for availability)
          }
        }

        // Create/update yard document with status "3" (Checkin started)
        try {
          const result = await createOrUpdateYardDocument(
            transportChargeToUse,
            transport.id,
            transport.truckLicensePlate ?? "",
            selectedLanguage,
            "3",
            undefined // SupplierDocumentNumber wird später gesetzt
          );
          // Speichere die neue YardDocument-Nummer im Transport-State
          transport.yardDocumentNumber = result.transportBatchNumber;
          setCheckinTransport(transport);

          // Verwende existierendes Kennzeichen aus PreCheckin, falls vorhanden
          if (result.existingLicensePlate) {
            setCheckinLicensePlate(result.existingLicensePlate);
            setIsLicensePlateFromPreCheckin(true);
            console.log("[CheckinCheckout] Using existing license plate from PreCheckin:", result.existingLicensePlate);
          } else {
            setCheckinLicensePlate("");
            setIsLicensePlateFromPreCheckin(false);
            console.log("[CheckinCheckout] No existing license plate from PreCheckin, user must enter manually");
          }

          console.log("[CheckinCheckout] Yard document created/updated with status 3 (Checkin started):", result.transportBatchNumber);
        } catch (error) {
          console.error("[CheckinCheckout] Failed to create/update yard document with status 3:", error);
          setCheckinBatchError(error instanceof Error ? error.message : t("status.transportLookupFailed"));
          return;
        }
      }

      // Weiter zum licensePlate Step (entweder mit 1 Batch vorausgefüllt, oder mit Auswahl bei mehreren)
      setCheckinStep("licensePlate");
    } catch (error) {
      const kind = (error as Error).name;
      if (kind === "NotFound") {
        setCheckinBatchError(t("status.transportNotFound"));
      } else {
        setCheckinBatchError(t("status.transportLookupFailed"));
      }
    } finally {
      setLoadingTransport(false);
    }
  }, [checkinBatchNumber, selectedLanguage, t]);

  const handleCheckinLicenseSubmit = useCallback(async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    console.log("[CheckinCheckout] License plate submit:", {
      licensePlate: checkinLicensePlate,
      availableBatchesCount: checkinAvailableBatches.length
    });

    // Validierung: Kennzeichen ist erforderlich bei mehreren Batches
    if (!checkinLicensePlate.trim() && checkinAvailableBatches.length > 1) {
      setCheckinLicenseError(t("validation.required"));
      console.log("[CheckinCheckout] License plate validation failed: empty with multiple batches");
      return;
    }

    setCheckinLicenseError(null);

    // Wenn es mehrere Batches gibt und noch kein YardDocument erstellt wurde
    if (checkinAvailableBatches.length > 1 && checkinTransport && !checkinTransport.yardDocumentNumber) {
      try {
        // Verwende die TRANSPORTCHARGE aus dem Transport-Objekt (nicht aus Input-Feld)
        const transportCharge = checkinTransport.transportBatchNumber;

        // Check if yardDocument already exists
        const searchUrl = `${API_BASE_URL}/api/yard-documents?filter[TRANSPORTCHARGE]=${encodeURIComponent(transportCharge)}`;
        const searchResponse = await fetch(searchUrl, {
          headers: {
            ...getAuthHeaders(),
            "Accept-Language": selectedLanguage
          },
          credentials: "include"
        });

        let existingYardDoc = null;
        let existingDocs: any[] = [];
        if (searchResponse.ok) {
          const docs = await searchResponse.json();
          if (Array.isArray(docs)) {
            existingDocs = docs;
            if (docs.length > 0) {
              existingYardDoc = docs[0];
            }
          }
        }

        // Check if quantityTrucks limit is reached
        if (checkinTransport.quantityTrucks && existingDocs.length >= checkinTransport.quantityTrucks) {
          setRedirectReason("precheckin-required");
          setRedirectCountdown(15);
          setStage("redirectToGuard");
          return;
        }

        // If NO yardDocument exists, validate PreCheckin requirement
        if (!existingYardDoc) {
          try {
            const validationUrl = `${API_BASE_URL}/api/batch-transports/validate-precheckin/${encodeURIComponent(transportCharge)}`;
            const validationResponse = await fetch(validationUrl, {
              headers: {
                ...getAuthHeaders(),
                "Accept-Language": selectedLanguage
              },
              credentials: "include"
            });

            if (validationResponse.ok) {
              const { preCheckinAllowed } = await validationResponse.json();

              // If PreCheckin is REQUIRED (PRECHECKIN = 'X'), redirect to guard
              if (preCheckinAllowed) {
                setRedirectReason("precheckin-required");
                setRedirectCountdown(15);
                setStage("redirectToGuard");
                return;
              }
            }
          } catch (validationError) {
            console.error("[CheckinCheckout] PreCheckin validation failed:", validationError);
          }
        }

        // Create/update yard document with status "3" (Checkin started)
        const result = await createOrUpdateYardDocument(
          transportCharge,
          checkinTransport.id,
          checkinLicensePlate,
          selectedLanguage,
          "3",
          undefined
        );

        // Update transport with yard document number
        const updatedTransport = { ...checkinTransport };
        updatedTransport.yardDocumentNumber = result.transportBatchNumber;
        setCheckinTransport(updatedTransport);

        // Verwende existierendes Kennzeichen aus PreCheckin, falls vorhanden
        if (result.existingLicensePlate) {
          setCheckinLicensePlate(result.existingLicensePlate);
          console.log("[CheckinCheckout] Using existing license plate from PreCheckin:", result.existingLicensePlate);
        }

        console.log("[CheckinCheckout] Yard document created/updated with status 3 (Checkin started):", result.transportBatchNumber);
      } catch (error) {
        console.error("[CheckinCheckout] Failed to create/update yard document with status 3:", error);
        setCheckinLicenseError(error instanceof Error ? error.message : "Fehler beim Erstellen des Yard-Documents");
        return;
      }
    }

    setCheckinStep("supplierDocument");
  }, [checkinLicensePlate, checkinAvailableBatches, checkinTransport, checkinBatchNumber, selectedLanguage, t]);

  const handleSupplierDocumentSubmit = useCallback((event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!supplierDocumentNumber.trim()) {
      setSupplierDocumentError(t("validation.required"));
      return;
    }

    setSupplierDocumentError(null);
    // Wenn kein Contract vorhanden: AssortmentDistribution erforderlich
    if (!checkinTransport?.Contract || checkinTransport.Contract.trim() === "") {
      setCheckinStep("assortmentDistribution");
      return;
    }

    // Contract vorhanden: direkt weiter
    if (checkinTransport?.requiresWasteSelection) {
      setCheckinStep("wasteDetails");
      return;
    }

    setCheckinStep("weightWaiting");
  }, [checkinTransport, supplierDocumentNumber, t]);

  const handleWasteSubmit = useCallback((event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const missingKey = !wasteKey.trim();
    const missingLocation = !loadingLocation.trim();

    if (missingKey || missingLocation) {
      setWasteError(t("validation.required"));
      return;
    }

    setWasteError(null);
    setCheckinStep("weightWaiting");
  }, [t, wasteKey, loadingLocation]);

  const handleAssortmentDistributionSave = useCallback(async () => {
    if (!checkinTransport?.id) return;

    try {
      // Nach dem Speichern der Sortimentsverteilung: Contract aus Bereitstellung holen
      const response = await fetch(
        `${API_BASE_URL}/api/batch-transports/${checkinTransport.id}/update-contract-from-staging`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          credentials: "include"
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to update contract: ${response.status}`);
      }

      const result = await response.json();
      console.log("Contract updated from staging:", result.Contract);

      // Aktualisiere lokalen State mit neuem Contract
      if (checkinTransport) {
        setCheckinTransport({
          ...checkinTransport,
          Contract: result.Contract
        });
      }

      // Weiter zum nächsten Step
      if (checkinTransport?.requiresWasteSelection) {
        setCheckinStep("wasteDetails");
      } else {
        setCheckinStep("weightWaiting");
      }
    } catch (error) {
      console.error("Error updating contract from staging:", error);
      // Falls Fehler: Trotzdem weitermachen (Contract bleibt leer)
      if (checkinTransport?.requiresWasteSelection) {
        setCheckinStep("wasteDetails");
      } else {
        setCheckinStep("weightWaiting");
      }
    }
  }, [checkinTransport]);

  // Auto-advance to success when checkin weight is confirmed
  useEffect(() => {
    if (stage === "checkin" && checkinStep === "weightWaiting" && checkinWeightConfirmation.phase === "confirmed") {
      setCheckinStep("success");

      // Update status to "6" (Checkin completed)
      if (checkinTransport) {
        createOrUpdateYardDocument(
          checkinTransport.transportBatchNumber,
          checkinTransport.id,
          checkinLicensePlate,
          selectedLanguage,
          "6",
          supplierDocumentNumber,
          wasteKey || undefined,
          loadingLocation || undefined
        )
          .then(() => {
            console.log("[CheckinCheckout] Yard document updated with status 6 (Checkin completed)");
          })
          .catch((error: unknown) => {
            console.error("[CheckinCheckout] Failed to update yard document with status 6:", error);
          });
      }
    }
  }, [stage, checkinStep, checkinWeightConfirmation.phase, checkinTransport, checkinLicensePlate, selectedLanguage]);

  const confirmCheckinWeight = useCallback(() => {
    if (checkinWeightConfirmation.phase !== "confirmed") {
      return;
    }
    setCheckinStep("success");
  }, [checkinWeightConfirmation.phase]);

  const retryCheckinWeightConfirmation = useCallback(() => {
    if (checkinWeightConfirmationPollRef.current != null) {
      window.clearInterval(checkinWeightConfirmationPollRef.current);
      checkinWeightConfirmationPollRef.current = null;
    }
    setCheckinWeightConfirmation({ id: null, phase: "idle", error: null });
  }, []);

  const handleCheckinBack = useCallback(() => {
    switch (checkinStep) {
      case "licensePlate": {
        setCheckinStep("batchNumber");
        break;
      }
      case "supplierDocument": {
        setCheckinStep("licensePlate");
        break;
      }
      case "assortmentDistribution": {
        setCheckinStep("supplierDocument");
        break;
      }
      case "wasteDetails": {
        // Zurück zu assortmentDistribution wenn Contract leer war, sonst zu supplierDocument
        if (!checkinTransport?.Contract || checkinTransport.Contract.trim() === "") {
          setCheckinStep("assortmentDistribution");
        } else {
          setCheckinStep("supplierDocument");
        }
        break;
      }
      case "weightWaiting":
      case "weightResult": {
        if (checkinTransport?.requiresWasteSelection) {
          setCheckinStep("wasteDetails");
        } else if (!checkinTransport?.Contract || checkinTransport.Contract.trim() === "") {
          setCheckinStep("assortmentDistribution");
        } else {
          setCheckinStep("supplierDocument");
        }
        break;
      }
      case "success": {
        resetFlows();
        break;
      }
      default: {
        setCheckinStep("batchNumber");
      }
    }
  }, [checkinStep, checkinTransport, resetFlows]);

  const handleCheckoutBatchSubmit = useCallback(async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = checkoutBatchNumber.trim();

    if (!trimmed) {
      setCheckoutBatchError(t("validation.required"));
      return;
    }

    setLoadingTransport(true);
    setCheckoutBatchError(null);

    try {
      // === Input Detection: LKW_LOAD_ID (16 Stellen) vs TRANSPORTCHARGE (13 Stellen) ===
      let transportChargeToUse = trimmed;
      let isLkwLoadId = false;

      // Wenn Input ≥ 16 Stellen: vermutlich LKW_LOAD_ID
      if (trimmed.length >= 16) {
        // Extrahiere TRANSPORTCHARGE direkt (erste 13 Stellen)
        transportChargeToUse = trimmed.substring(0, 13);
        isLkwLoadId = true;
        console.log("[CheckinCheckout] Checkout: Input detected as LKW_LOAD_ID (≥16 chars), extracted TRANSPORTCHARGE:", transportChargeToUse);
      }

      console.log("[CheckinCheckout] Checkout: Using TRANSPORTCHARGE for lookup:", { originalInput: trimmed, transportCharge: transportChargeToUse, isLkwLoadId });

      // Lade ALLE BatchTransports mit dieser TRANSPORTCHARGE
      const allBatches = await loadAllBatchTransports(transportChargeToUse, selectedLanguage);

      // Speichere alle gefundenen Batches
      setCheckoutAvailableBatches(allBatches);

      // Wenn nur 1 Batch gefunden: direkt verwenden und YardDocument erstellen
      if (allBatches.length === 1) {
        const transport = allBatches[0];
        setCheckoutTransport(transport);
        setCheckoutLicensePlate(transport.truckLicensePlate ?? "");

        // Create/update yard document with status "9" (Checkout started)
        try {
          const result = await createOrUpdateYardDocument(
            transportChargeToUse,
            transport.id,
            transport.truckLicensePlate ?? "",
            selectedLanguage,
            "9",
            undefined // No supplier document in checkout
          );
          // Speichere die neue YardDocument-Nummer im Transport-State
          transport.yardDocumentNumber = result.transportBatchNumber;
          setCheckoutTransport(transport);

          // Verwende existierendes Kennzeichen aus PreCheckin/Checkin, falls vorhanden
          if (result.existingLicensePlate) {
            setCheckoutLicensePlate(result.existingLicensePlate);
            console.log("[CheckinCheckout] Using existing license plate from previous stage:", result.existingLicensePlate);
          }

          console.log("[CheckinCheckout] Yard document created/updated with status 9 (Checkout started):", result.transportBatchNumber);
        } catch (error) {
          console.error("[CheckinCheckout] Failed to create/update yard document with status 9:", error);
          setCheckoutBatchError(error instanceof Error ? error.message : t("status.transportLookupFailed"));
          return;
        }
      }

      // Weiter zum licensePlate Step (entweder mit 1 Batch vorausgefüllt, oder mit Auswahl bei mehreren)
      setCheckoutStep("licensePlate");
    } catch (error) {
      const kind = (error as Error).name;
      if (kind === "NotFound") {
        setCheckoutBatchError(t("status.transportNotFound"));
      } else {
        setCheckoutBatchError(t("status.transportLookupFailed"));
      }
    } finally {
      setLoadingTransport(false);
    }
  }, [checkoutBatchNumber, selectedLanguage, t]);

  const handleCheckoutLicenseSubmit = useCallback(async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!checkoutLicensePlate.trim() && checkoutAvailableBatches.length > 1) {
      setCheckoutLicenseError(t("validation.required"));
      return;
    }

    setCheckoutLicenseError(null);

    // Wenn es mehrere Batches gibt und noch kein YardDocument erstellt wurde
    if (checkoutAvailableBatches.length > 1 && checkoutTransport && !checkoutTransport.yardDocumentNumber) {
      try {
        // Verwende die TRANSPORTCHARGE aus dem Transport-Objekt (nicht aus Input-Feld)
        const transportCharge = checkoutTransport.transportBatchNumber;

        // Create/update yard document with status "9" (Checkout started)
        const result = await createOrUpdateYardDocument(
          transportCharge,
          checkoutTransport.id,
          checkoutLicensePlate,
          selectedLanguage,
          "9",
          undefined
        );

        // Update transport with yard document number
        const updatedTransport = { ...checkoutTransport };
        updatedTransport.yardDocumentNumber = result.transportBatchNumber;
        setCheckoutTransport(updatedTransport);

        // Verwende existierendes Kennzeichen aus PreCheckin/Checkin, falls vorhanden
        if (result.existingLicensePlate) {
          setCheckoutLicensePlate(result.existingLicensePlate);
          console.log("[CheckinCheckout] Using existing license plate from previous stage:", result.existingLicensePlate);
        }

        console.log("[CheckinCheckout] Yard document created/updated with status 9 (Checkout started):", result.transportBatchNumber);
      } catch (error) {
        console.error("[CheckinCheckout] Failed to create/update yard document with status 9:", error);
        setCheckoutLicenseError(error instanceof Error ? error.message : "Fehler beim Erstellen des Yard-Documents");
        return;
      }
    }

    setCheckoutStep("weightWaiting");
  }, [checkoutLicensePlate, checkoutAvailableBatches, checkoutTransport, checkoutBatchNumber, selectedLanguage, t]);

  // Auto-advance to signature when checkout weight is confirmed
  useEffect(() => {
    if (stage === "checkout" && checkoutStep === "weightWaiting" && checkoutWeightConfirmation.phase === "confirmed") {
      setCheckoutStep("signature");
    }
  }, [stage, checkoutStep, checkoutWeightConfirmation.phase]);

  const confirmCheckoutWeight = useCallback(() => {
    if (checkoutWeightConfirmation.phase !== "confirmed") {
      return;
    }
    setCheckoutStep("signature");
  }, [checkoutWeightConfirmation.phase]);

  const retryCheckoutWeightConfirmation = useCallback(() => {
    if (checkoutWeightConfirmationPollRef.current != null) {
      window.clearInterval(checkoutWeightConfirmationPollRef.current);
      checkoutWeightConfirmationPollRef.current = null;
    }
    setCheckoutWeightConfirmation({ id: null, phase: "idle", error: null });
  }, []);

  const completeCheckoutSignature = useCallback(() => {
    if (!hasSignature) {
      setSignatureError(t("checkout.signature.error"));
      return;
    }

    setSignatureError(null);
    setCheckoutStep("success");

    // Update status to "12" (Checkout completed)
    if (checkoutTransport) {
      createOrUpdateYardDocument(
        checkoutTransport.transportBatchNumber,
        checkoutTransport.id,
        checkoutLicensePlate,
        selectedLanguage,
        "12",
        undefined, // No supplier document in checkout
        undefined, // No waste key in checkout
        undefined  // No loading location in checkout
      )
        .then(() => {
          console.log("[CheckinCheckout] Yard document updated with status 12 (Checkout completed)");
        })
        .catch((error: unknown) => {
          console.error("[CheckinCheckout] Failed to update yard document with status 12:", error);
        });
    }
  }, [hasSignature, t, checkoutTransport, checkoutLicensePlate, selectedLanguage]);

  const handleCheckoutBack = useCallback(() => {
    switch (checkoutStep) {
      case "licensePlate": {
        setCheckoutStep("batchNumber");
        break;
      }
      case "weightWaiting":
      case "weightResult": {
        setCheckoutStep("licensePlate");
        break;
      }
      case "signature": {
        setCheckoutStep("weightResult");
        break;
      }
      case "success": {
        resetFlows();
        break;
      }
      default: {
        setCheckoutStep("batchNumber");
      }
    }
  }, [checkoutStep, resetFlows]);

  useEffect(() => {
    if (stage === "checkin" && checkinStep === "weightWaiting") {
      setCheckinWeight(null);
      const timer = window.setTimeout(() => {
        const weight = generateRandomWeight();
        setCheckinWeight(weight);
      }, WEIGHT_DELAY_MS);

      return () => window.clearTimeout(timer);
    }
  }, [stage, checkinStep]);

  useEffect(() => {
    if (stage === "checkout" && checkoutStep === "weightWaiting") {
      setCheckoutWeight(null);
      const timer = window.setTimeout(() => {
        const weight = generateRandomWeight();
        setCheckoutWeight(weight);
      }, WEIGHT_DELAY_MS);

      return () => window.clearTimeout(timer);
    }
  }, [stage, checkoutStep]);

  useEffect(() => {
    if (stage === "checkout" && checkoutStep === "signature") {
      setHasSignature(false);
      hasStrokeRef.current = false;
      const canvas = signatureCanvasRef.current;
      if (canvas) {
        const dpr = window.devicePixelRatio ?? 1;
        const width = canvas.clientWidth;
        const height = canvas.clientHeight;
        canvas.width = width * dpr;
        canvas.height = height * dpr;
        const context = canvas.getContext("2d");
        if (context) {
          context.setTransform(dpr, 0, 0, dpr, 0, 0);
          context.lineWidth = 2.5;
          context.lineCap = "round";
          context.strokeStyle = "#1f3150";
          context.clearRect(0, 0, width, height);
        }
      }
    }
  }, [stage, checkoutStep]);

  const clearSignature = useCallback(() => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) {
      return;
    }

    const context = canvas.getContext("2d");
    if (context) {
      context.clearRect(0, 0, canvas.width, canvas.height);
    }

    hasStrokeRef.current = false;
    setHasSignature(false);
    setSignatureError(null);
  }, []);

  const extractWeightConfirmationError = async (response: Response): Promise<string> => {
    try {
      const contentType = response.headers.get("content-type") ?? "";

      if (contentType.includes("application/json")) {
        const payload = (await response.json()) as Record<string, unknown> | null;
        const message = typeof payload?.message === "string" ? payload.message.trim() : "";
        if (message) {
          return message;
        }
      } else {
        const text = await response.text();
        const trimmed = text.trim();
        if (trimmed) {
          return trimmed;
        }
      }
    } catch {
      // ignore parse failures
    }

    return "";
  };

  const submitWeightConfirmationRequest = useCallback(async (
    payload: WeightConfirmationPayload
  ): Promise<WeightConfirmationResponse> => {
    console.log("[DEBUG] Submitting weight confirmation:", payload);
    console.log("[DEBUG] API URL:", `${API_BASE_URL}/api/yard/weight-confirmations`);

    const response = await fetch(`${API_BASE_URL}/api/yard/weight-confirmations`, {
      method: "POST",
      credentials: "include",
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
        "Accept-Language": selectedLanguage
      },
      body: JSON.stringify(payload)
    });

    console.log("[DEBUG] Response status:", response.status);
    console.log("[DEBUG] Response ok:", response.ok);

    if (!response.ok) {
      const errorText = await response.text();
      console.error("[DEBUG] Error response:", errorText);
      const message = await extractWeightConfirmationError(response);
      throw new Error(message || t("status.weightConfirmationError"));
    }

    const jsonResponse = await response.json();
    console.log("[DEBUG] Success response:", jsonResponse);
    return jsonResponse as Promise<WeightConfirmationResponse>;
  }, [selectedLanguage, t]);

  const fetchWeightConfirmationStatus = useCallback(async (id: string): Promise<WeightConfirmationResponse> => {
    const response = await fetch(
      `${API_BASE_URL}/api/yard/weight-confirmations/${encodeURIComponent(id)}`,
      {
        credentials: "include",
        headers: {
          ...getAuthHeaders(),
          "Accept-Language": selectedLanguage
        }
      }
    );

    if (response.status === 404) {
      throw new Error(t("status.weightConfirmationNotFound"));
    }

    if (!response.ok) {
      const message = await extractWeightConfirmationError(response);
      throw new Error(message || t("status.weightConfirmationError"));
    }

    return response.json() as Promise<WeightConfirmationResponse>;
  }, [selectedLanguage, t]);

  // Create weight confirmation when weight is generated for checkin
  useEffect(() => {
    if (stage === "checkin" && checkinStep === "weightWaiting" && checkinWeight != null) {
      if (checkinWeightConfirmation.phase === "idle") {
        // Verwende die hochgezählte YardDocument-Nummer (falls vorhanden), sonst die ursprüngliche BatchTransport-Nummer
        const transportNumber = (checkinTransport?.yardDocumentNumber ?? checkinTransport?.transportBatchNumber ?? checkinBatchNumber).trim();

        if (!transportNumber) {
          setCheckinWeightConfirmation({ id: null, phase: "error", error: t("status.weightConfirmationMissingBatch") });
          return;
        }

        const licensePlate = checkinLicensePlate.trim();

        setCheckinWeightConfirmation({ id: null, phase: "creating", error: null });

        const payload = {
          transportBatchNumber: transportNumber,
          direction: "checkin" as const,
          weight: checkinWeight,
          licensePlate: licensePlate.length > 0 ? licensePlate : null,
          batchTransportId: checkinTransport?.id ?? null
        };

        console.log("[CheckinCheckout] Creating weight confirmation:", payload);

        void submitWeightConfirmationRequest(payload)
          .then((response) => {
            console.log("[CheckinCheckout] Weight confirmation created:", response);
            setCheckinWeightConfirmation({
              id: response.id,
              phase: response.status === "confirmed" ? "confirmed" : "pending",
              error: null
            });
          })
          .catch((error) => {
            const message = error instanceof Error ? error.message : t("status.weightConfirmationError");
            console.error("[CheckinCheckout] Weight confirmation failed:", error);
            setCheckinWeightConfirmation({ id: null, phase: "error", error: message || t("status.weightConfirmationError") });
          });
      }
    } else if (stage !== "checkin" || checkinStep !== "weightWaiting") {
      // Only reset if not in confirmed state (allow confirmed to persist for auto-advance)
      if (checkinWeightConfirmation.phase !== "confirmed") {
        if (checkinWeightConfirmationPollRef.current != null) {
          window.clearInterval(checkinWeightConfirmationPollRef.current);
          checkinWeightConfirmationPollRef.current = null;
        }
        setCheckinWeightConfirmation({ id: null, phase: "idle", error: null });
      }
    }
  }, [
    stage,
    checkinStep,
    checkinWeight,
    checkinTransport,
    checkinBatchNumber,
    checkinLicensePlate,
    checkinWeightConfirmation.phase,
    submitWeightConfirmationRequest,
    t
  ]);

  // Poll weight confirmation status for checkin and auto-advance to success when confirmed
  useEffect(() => {
    if (stage !== "checkin" || checkinStep !== "weightWaiting") {
      if (checkinWeightConfirmationPollRef.current != null) {
        window.clearInterval(checkinWeightConfirmationPollRef.current);
        checkinWeightConfirmationPollRef.current = null;
      }
      return;
    }

    if (checkinWeightConfirmation.phase !== "pending" || !checkinWeightConfirmation.id) {
      if (checkinWeightConfirmationPollRef.current != null) {
        window.clearInterval(checkinWeightConfirmationPollRef.current);
        checkinWeightConfirmationPollRef.current = null;
      }
      return;
    }

    if (checkinWeightConfirmationPollRef.current != null) {
      return;
    }

    const tick = async () => {
      try {
        const response = await fetchWeightConfirmationStatus(checkinWeightConfirmation.id!);
        if (response.status === "confirmed") {
          setCheckinWeightConfirmation({ id: response.id, phase: "confirmed", error: null });
        } else if (response.status === "superseded") {
          setCheckinWeightConfirmation({ id: response.id, phase: "error", error: t("status.weightConfirmationSuperseded") });
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : t("status.weightConfirmationError");
        setCheckinWeightConfirmation({ id: null, phase: "error", error: message || t("status.weightConfirmationError") });
        if (checkinWeightConfirmationPollRef.current != null) {
          window.clearInterval(checkinWeightConfirmationPollRef.current);
          checkinWeightConfirmationPollRef.current = null;
        }
      }
    };

    void tick();
    const intervalId = window.setInterval(() => {
      void tick();
    }, WEIGHT_CONFIRMATION_POLL_INTERVAL_MS);
    checkinWeightConfirmationPollRef.current = intervalId;

    return () => {
      window.clearInterval(intervalId);
      checkinWeightConfirmationPollRef.current = null;
    };
  }, [
    stage,
    checkinStep,
    checkinWeightConfirmation.phase,
    checkinWeightConfirmation.id,
    fetchWeightConfirmationStatus,
    t
  ]);

  // Create weight confirmation when weight is generated for checkout
  useEffect(() => {
    if (stage === "checkout" && checkoutStep === "weightWaiting" && checkoutWeight != null) {
      if (checkoutWeightConfirmation.phase === "idle") {
        // Verwende die hochgezählte YardDocument-Nummer (falls vorhanden), sonst die ursprüngliche BatchTransport-Nummer
        const transportNumber = (checkoutTransport?.yardDocumentNumber ?? checkoutTransport?.transportBatchNumber ?? checkoutBatchNumber).trim();

        if (!transportNumber) {
          setCheckoutWeightConfirmation({ id: null, phase: "error", error: t("status.weightConfirmationMissingBatch") });
          return;
        }

        const licensePlate = checkoutLicensePlate.trim();

        setCheckoutWeightConfirmation({ id: null, phase: "creating", error: null });

        const payload = {
          transportBatchNumber: transportNumber,
          direction: "checkout" as const,
          weight: checkoutWeight,
          licensePlate: licensePlate.length > 0 ? licensePlate : null,
          batchTransportId: checkoutTransport?.id ?? null
        };

        console.log("[CheckinCheckout] Creating weight confirmation (checkout):", payload);

        void submitWeightConfirmationRequest(payload)
          .then((response) => {
            console.log("[CheckinCheckout] Weight confirmation created (checkout):", response);
            setCheckoutWeightConfirmation({
              id: response.id,
              phase: response.status === "confirmed" ? "confirmed" : "pending",
              error: null
            });
          })
          .catch((error) => {
            const message = error instanceof Error ? error.message : t("status.weightConfirmationError");
            console.error("[CheckinCheckout] Weight confirmation failed (checkout):", error);
            setCheckoutWeightConfirmation({ id: null, phase: "error", error: message || t("status.weightConfirmationError") });
          });
      }
    } else if (stage !== "checkout" || checkoutStep !== "weightWaiting") {
      // Only reset if not in confirmed state (allow confirmed to persist for auto-advance)
      if (checkoutWeightConfirmation.phase !== "confirmed") {
        if (checkoutWeightConfirmationPollRef.current != null) {
          window.clearInterval(checkoutWeightConfirmationPollRef.current);
          checkoutWeightConfirmationPollRef.current = null;
        }
        setCheckoutWeightConfirmation({ id: null, phase: "idle", error: null });
      }
    }
  }, [
    stage,
    checkoutStep,
    checkoutWeight,
    checkoutTransport,
    checkoutBatchNumber,
    checkoutLicensePlate,
    checkoutWeightConfirmation.phase,
    submitWeightConfirmationRequest,
    t
  ]);

  // Poll weight confirmation status for checkout and auto-advance to signature when confirmed
  useEffect(() => {
    if (stage !== "checkout" || checkoutStep !== "weightWaiting") {
      if (checkoutWeightConfirmationPollRef.current != null) {
        window.clearInterval(checkoutWeightConfirmationPollRef.current);
        checkoutWeightConfirmationPollRef.current = null;
      }
      return;
    }

    if (checkoutWeightConfirmation.phase !== "pending" || !checkoutWeightConfirmation.id) {
      if (checkoutWeightConfirmationPollRef.current != null) {
        window.clearInterval(checkoutWeightConfirmationPollRef.current);
        checkoutWeightConfirmationPollRef.current = null;
      }
      return;
    }

    if (checkoutWeightConfirmationPollRef.current != null) {
      return;
    }

    const tick = async () => {
      try {
        const response = await fetchWeightConfirmationStatus(checkoutWeightConfirmation.id!);
        if (response.status === "confirmed") {
          setCheckoutWeightConfirmation({ id: response.id, phase: "confirmed", error: null });
        } else if (response.status === "superseded") {
          setCheckoutWeightConfirmation({ id: response.id, phase: "error", error: t("status.weightConfirmationSuperseded") });
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : t("status.weightConfirmationError");
        setCheckoutWeightConfirmation({ id: null, phase: "error", error: message || t("status.weightConfirmationError") });
        if (checkoutWeightConfirmationPollRef.current != null) {
          window.clearInterval(checkoutWeightConfirmationPollRef.current);
          checkoutWeightConfirmationPollRef.current = null;
        }
      }
    };

    void tick();
    const intervalId = window.setInterval(() => {
      void tick();
    }, WEIGHT_CONFIRMATION_POLL_INTERVAL_MS);
    checkoutWeightConfirmationPollRef.current = intervalId;

    return () => {
      window.clearInterval(intervalId);
      checkoutWeightConfirmationPollRef.current = null;
    };
  }, [
    stage,
    checkoutStep,
    checkoutWeightConfirmation.phase,
    checkoutWeightConfirmation.id,
    fetchWeightConfirmationStatus,
    t
  ]);

  useEffect(() => {
    const isSuccessStage =
      (stage === "checkin" && checkinStep === "success") ||
      (stage === "checkout" && checkoutStep === "success");

    if (!isSuccessStage) {
      setAutoResetSecondsRemaining((current) => (current !== null ? null : current));
      return;
    }

    setAutoResetSecondsRemaining(RESET_DURATION_SECONDS);

    const intervalId = window.setInterval(() => {
      setAutoResetSecondsRemaining((current) => {
        if (current == null) {
          return current;
        }

        if (current <= 1) {
          return 0;
        }

        return current - 1;
      });
    }, 1000);

    const timeoutId = window.setTimeout(() => {
      resetFlows();
    }, RESET_DURATION_SECONDS * 1000);

    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };
  }, [stage, checkinStep, checkoutStep, resetFlows]);

  // Redirect countdown for redirectToGuard stage
  useEffect(() => {
    if (stage !== "redirectToGuard") {
      return;
    }

    const intervalId = window.setInterval(() => {
      setRedirectCountdown((current) => {
        if (current <= 1) {
          return 0;
        }
        return current - 1;
      });
    }, 1000);

    const timeoutId = window.setTimeout(() => {
      resetFlows();
    }, redirectCountdown * 1000);

    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };
  }, [stage, redirectCountdown, resetFlows]);

  // Close language menu when clicking outside
  useEffect(() => {
    if (!showLanguageMenu) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.language-switcher')) {
        setShowLanguageMenu(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showLanguageMenu]);

  const renderAutoResetNotice = () => {
    if (autoResetSecondsRemaining == null) {
      return null;
    }

    const seconds = Math.max(0, autoResetSecondsRemaining);
    const progress = Math.max(0, Math.min(100, (seconds / RESET_DURATION_SECONDS) * 100));
    const message = t("status.autoReset").replace("{seconds}", seconds.toString());

    return (
      <div className="auto-reset-notice" role="status">
        <span>{message}</span>
        <div className="auto-reset-track" aria-hidden="true">
          <div className="auto-reset-progress" style={{ width: `${progress}%` }} />
        </div>
      </div>
    );
  };

  const getPointerCoordinates = useCallback((event: ReactPointerEvent<HTMLCanvasElement>) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) {
      return { x: 0, y: 0 };
    }

    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };
  }, []);

  const handlePointerDown = useCallback((event: ReactPointerEvent<HTMLCanvasElement>) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) {
      return;
    }

    const context = canvas.getContext("2d");
    if (!context) {
      return;
    }

    event.preventDefault();
    canvas.setPointerCapture?.(event.pointerId);
    const { x, y } = getPointerCoordinates(event);
    context.beginPath();
    context.moveTo(x, y);
    isDrawingRef.current = true;
  }, [getPointerCoordinates]);

  const handlePointerMove = useCallback((event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!isDrawingRef.current) {
      return;
    }

    const canvas = signatureCanvasRef.current;
    if (!canvas) {
      return;
    }

    const context = canvas.getContext("2d");
    if (!context) {
      return;
    }

    event.preventDefault();
    const { x, y } = getPointerCoordinates(event);
    context.lineTo(x, y);
    context.stroke();
    hasStrokeRef.current = true;
    setHasSignature(true);
  }, [getPointerCoordinates]);

  const handlePointerUp = useCallback((event: ReactPointerEvent<HTMLCanvasElement>) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) {
      return;
    }

    event.preventDefault();
    canvas.releasePointerCapture?.(event.pointerId);
    isDrawingRef.current = false;
  }, []);

  const renderWelcomeScreen = () => (
    <section className="step-content">
      <div className="welcome-screen">
        <img src={PfleidererLogo} alt="Pfleiderer Logo" className="welcome-logo" />
        <h1 className="welcome-title">Willkommen bei Pfleiderer</h1>
        <button
          type="button"
          className="welcome-button"
          onClick={() => setStage("language")}
        >
          Start
        </button>
      </div>
    </section>
  );

  const renderLanguageSelection = () => (
    <section className="step-content">
      <div className="step-info">
        <h2 className="step-title">{t("language.title")}</h2>
        <p className="step-subtitle">{t("language.subtitle")}</p>
      </div>
      <div className="language-grid">
        {SUPPORTED_LANGUAGES.map((language) => (
          <button
            key={language.key}
            type="button"
            className={`language-card${selectedLanguage === language.key ? " selected" : ""}`}
            onClick={() => handleLanguageSelect(language.key)}
          >
            <span className={`fi fi-${language.flagCode}`} aria-hidden="true" />
            <span>{language.label}</span>
          </button>
        ))}
      </div>
    </section>
  );

  const renderRedirectToGuard = () => {
    const message = redirectReason === "precheckin-required"
      ? t("redirect.preCheckinMessage")
      : "";
    const title = redirectReason === "precheckin-required"
      ? t("redirect.preCheckinTitle")
      : "";

    return (
      <section className="step-content">
        <div className="redirect-notice">
          <h2 className="redirect-title">{title}</h2>
          <p className="redirect-message">{message}</p>
          <div className="redirect-countdown">
            {t("redirect.countdown").replace("{seconds}", redirectCountdown.toString())}
          </div>
        </div>
        <div className="step-actions">
          <button className="primary" type="button" onClick={resetFlows}>
            {t("redirect.confirm")}
          </button>
        </div>
      </section>
    );
  };

  const renderModeSelection = () => (
    <section className="step-content">
      <div className="step-info">
        <h2 className="step-title">{t("mode.title")}</h2>
        <p className="step-subtitle">{t("mode.description")}</p>
      </div>
      <div className="mode-options">
        <button
          type="button"
          className="mode-card"
          onClick={() => {
            setStage("checkin");
            setCheckinStep("batchNumber");
          }}
        >
          <h2>{t("mode.checkin")}</h2>
        </button>
        <button
          type="button"
          className="mode-card"
          onClick={() => {
            setStage("checkout");
            setCheckoutStep("batchNumber");
          }}
        >
          <h2>{t("mode.checkout")}</h2>
        </button>
      </div>
    </section>
  );

  const renderCheckinContent = () => {
    if (checkinStep === "batchNumber") {
      return (
        <form className="step-form" onSubmit={handleCheckinBatchSubmit}>
          <div>
            <h2 className="step-title">{t("checkin.batch.title")}</h2>
            <p className="step-subtitle">{t("checkin.batch.subtitle")}</p>
          </div>
          <label>
            {t("checkin.batch.placeholder")}
            <input
              value={checkinBatchNumber}
              onChange={(event) => setCheckinBatchNumber(event.target.value)}
              placeholder={t("checkin.batch.placeholder")}
              disabled={loadingTransport}
            />
          </label>
          {checkinBatchError ? <div className="step-warning">{checkinBatchError}</div> : null}
          <div className="step-actions">
            <button className="secondary" type="button" onClick={goToModeSelection}>
              {t("actions.back")}
            </button>
            <button className="primary" type="submit" disabled={loadingTransport}>
              {loadingTransport ? t("actions.confirm") : t("actions.continue")}
            </button>
          </div>
        </form>
      );
    }

    if (checkinStep === "licensePlate") {
      const hasSingleBatch = checkinAvailableBatches.length === 1;
      const hasMultipleBatches = checkinAvailableBatches.length > 1;

      console.log("[CheckinCheckout] License plate input:", {
        hasSingleBatch,
        hasMultipleBatches,
        isFromPreCheckin: isLicensePlateFromPreCheckin,
        licensePlate: checkinLicensePlate,
        availableBatchesCount: checkinAvailableBatches.length
      });

      return (
        <form className="step-form" onSubmit={handleCheckinLicenseSubmit}>
          <div>
            <h2 className="step-title">{t("checkin.license.title")}</h2>
            <p className="step-subtitle">
              {hasMultipleBatches
                ? "Mehrere Transportchargen gefunden. Bitte wählen Sie das Kennzeichen aus."
                : t("checkin.license.subtitle")}
            </p>
          </div>

          {hasSingleBatch ? (
            // Einzelne Transportcharge: Nur ausgegraut wenn Kennzeichen aus PreCheckin stammt
            <label>
              {t("checkin.license.placeholder")}
              <input
                value={checkinLicensePlate}
                onChange={(event) => {
                  // Nur Buchstaben und Zahlen erlauben (keine Sonderzeichen, keine Leerzeichen, keine Bindestriche)
                  const sanitized = event.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
                  setCheckinLicensePlate(sanitized);
                  console.log("[CheckinCheckout] License plate changed:", sanitized);
                }}
                placeholder={t("checkin.license.placeholder")}
                disabled={isLicensePlateFromPreCheckin}
                style={isLicensePlateFromPreCheckin ? { backgroundColor: "#f3f4f6", cursor: "not-allowed" } : {}}
                autoFocus={!isLicensePlateFromPreCheckin}
              />
            </label>
          ) : hasMultipleBatches ? (
            // Mehrere Transportchargen: Combobox zur Auswahl
            <label>
              {t("checkin.license.placeholder")}
              <select
                value={checkinLicensePlate}
                onChange={(event) => {
                  const selectedLicensePlate = event.target.value;
                  setCheckinLicensePlate(selectedLicensePlate);

                  // Lade die entsprechende Transportcharge
                  const selectedBatch = checkinAvailableBatches.find(
                    batch => batch.truckLicensePlate === selectedLicensePlate
                  );

                  if (selectedBatch) {
                    setCheckinTransport(selectedBatch);
                    setSupplierDocumentNumber(selectedBatch.supplierReferenceNumber ?? "");
                    setWasteKey(selectedBatch.wasteKey ?? "");
                    setLoadingLocation(selectedBatch.loadingLocation ?? "");

                    // Load LIFNR from Contract for loading locations
                    if (selectedBatch.Contract) {
                      fetchLIFNRByContractNumber(selectedBatch.Contract)
                        .then(setLifnr)
                        .catch((error) => {
                          console.error("Failed to load LIFNR from contract:", error);
                          setLifnr(null);
                        });
                    } else {
                      setLifnr(null);
                    }
                  }
                }}
                style={{ padding: "12px", fontSize: "16px" }}
              >
                <option value="">-- Kennzeichen auswählen --</option>
                {checkinAvailableBatches.map((batch) => {
                  const batchSuffix = batch.transportBatchNumber.slice(-3);
                  const displayText = batch.truckLicensePlate
                    ? `${batch.truckLicensePlate} (${batchSuffix})`
                    : `Kein Kennzeichen (${batchSuffix})`;

                  return (
                    <option key={batch.id} value={batch.truckLicensePlate ?? ""}>
                      {displayText}
                    </option>
                  );
                })}
              </select>
            </label>
          ) : (
            // Fallback: Manuelle Eingabe (sollte nicht vorkommen, da mindestens 1 Batch existiert)
            <label>
              {t("checkin.license.placeholder")}
              <input
                value={checkinLicensePlate}
                onChange={(event) => {
                  // Nur Buchstaben und Zahlen erlauben (keine Sonderzeichen, keine Leerzeichen, keine Bindestriche)
                  const sanitized = event.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
                  setCheckinLicensePlate(sanitized);
                }}
                placeholder={t("checkin.license.placeholder")}
              />
            </label>
          )}

          {checkinLicenseError ? <div className="step-warning">{checkinLicenseError}</div> : null}
          <div className="step-actions">
            <button className="secondary" type="button" onClick={handleCheckinBack}>
              {t("actions.back")}
            </button>
            <button className="primary" type="submit">
              {t("actions.continue")}
            </button>
          </div>
        </form>
      );
    }

    if (checkinStep === "supplierDocument") {
      return (
        <form className="step-form" onSubmit={handleSupplierDocumentSubmit}>
          <div>
            <h2 className="step-title">{t("checkin.supplier.title")}</h2>
            <p className="step-subtitle">{t("checkin.supplier.subtitle")}</p>
          </div>
          <label>
            {t("checkin.supplier.placeholder")}
            <input
              value={supplierDocumentNumber}
              onChange={(event) => setSupplierDocumentNumber(event.target.value)}
              placeholder={t("checkin.supplier.placeholder")}
              autoFocus
            />
          </label>
          {supplierDocumentError ? <div className="step-warning">{supplierDocumentError}</div> : null}
          <div className="step-actions">
            <button className="secondary" type="button" onClick={handleCheckinBack}>
              {t("actions.back")}
            </button>
            <button className="primary" type="submit">
              {t("actions.continue")}
            </button>
          </div>
        </form>
      );
    }

    if (checkinStep === "assortmentDistribution") {
      if (!checkinTransport?.id) {
        return (
          <div className="step-form">
            <div className="step-warning">Fehler: Kein BatchTransport geladen</div>
            <div className="step-actions">
              <button className="secondary" type="button" onClick={handleCheckinBack}>
                {t("actions.back")}
              </button>
            </div>
          </div>
        );
      }

      return (
        <div className="step-form" style={{ maxWidth: "900px" }}>
          <div>
            <h2 className="step-title">{t("checkin.assortment.title")}</h2>
            <p className="step-subtitle">{t("checkin.assortment.subtitle")}</p>
          </div>
          <AssortmentDistributionView
            batchTransportId={checkinTransport.id}
            onClose={() => {
              // Back button wird über handleCheckinBack gehandelt
            }}
            onSave={handleAssortmentDistributionSave}
          />
          <div className="step-actions" style={{ marginTop: "20px" }}>
            <button className="secondary" type="button" onClick={handleCheckinBack}>
              {t("actions.back")}
            </button>
          </div>
        </div>
      );
    }

    if (checkinStep === "wasteDetails") {
      return (
        <form className="step-form" onSubmit={handleWasteSubmit}>
          <div>
            <h2 className="step-title">{t("checkin.waste.title")}</h2>
            <p className="step-subtitle">{t("checkin.waste.subtitle")}</p>
          </div>
          <label>
            {t("checkin.waste.keyLabel")}
            <input
              value={wasteKey}
              onChange={(event) => setWasteKey(event.target.value)}
              placeholder={t("checkin.waste.keyLabel")}
            />
          </label>
          <label style={{ position: "relative" }}>
            {t("checkin.waste.locationLabel")}
            <input
              ref={locationInputRef}
              value={loadingLocation}
              onChange={(event) => {
                setLoadingLocation(event.target.value);
                setLocationSearchTerm(event.target.value);
              }}
              onFocus={() => {
                setShowLocationDropdown(true);
                // If no LIFNR (no contract), immediately load all secondary locations
                if (!lifnr && secondaryLocations.length === 0) {
                  const loadAllLocations = async () => {
                    setLoadingSecondary(true);
                    try {
                      const locations = await searchSecondaryLoadingLocations("", 50);
                      setSecondaryLocations(locations);
                    } catch (error) {
                      console.error("Failed to load all loading locations:", error);
                    } finally {
                      setLoadingSecondary(false);
                    }
                  };
                  void loadAllLocations();
                }
                setLocationSearchTerm(loadingLocation);
              }}
              placeholder={t("checkin.waste.locationLabel")}
            />
            {/* Dropdown mit ValueHelp */}
            {showLocationDropdown && (
              <div
                ref={locationDropdownRef}
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  backgroundColor: "white",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  maxHeight: "300px",
                  overflowY: "auto",
                  zIndex: 1000,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
                }}
              >
                {loadingPrimary || loadingSecondary ? (
                  <div style={{ padding: "12px", textAlign: "center", color: "#6b7280" }}>
                    Lädt...
                  </div>
                ) : (
                  <>
                    {/* Primäre Ladeorte (aus ZHB_T24) */}
                    {primaryLocations.filter(location =>
                      !locationSearchTerm ||
                      locationSearchTerm.length < 2 ||
                      location.displayText.toLowerCase().includes(locationSearchTerm.toLowerCase())
                    ).length > 0 && (
                        <>
                          <div style={{
                            padding: "8px 12px",
                            fontSize: "11px",
                            fontWeight: 600,
                            color: "#6b7280",
                            textTransform: "uppercase",
                            letterSpacing: "0.4px",
                            backgroundColor: "#f9fafb",
                            borderBottom: "1px solid #e5e7eb"
                          }}>
                            Zugeordnete Ladeorte
                          </div>
                          {primaryLocations.filter(location =>
                            !locationSearchTerm ||
                            locationSearchTerm.length < 2 ||
                            location.displayText.toLowerCase().includes(locationSearchTerm.toLowerCase())
                          ).map((location) => (
                            <div
                              key={location.LADEORTNR}
                              style={{
                                padding: "10px 12px",
                                cursor: "pointer",
                                borderBottom: "1px solid #f3f4f6"
                              }}
                              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f3f4f6")}
                              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                              onClick={() => {
                                setLoadingLocation(location.displayText);
                                setLocationSearchTerm("");
                                setShowLocationDropdown(false);
                              }}
                            >
                              <div style={{ fontSize: "13px", color: "#1e293b" }}>
                                {location.displayText}
                              </div>
                            </div>
                          ))}
                        </>
                      )}

                    {/* Sekundäre Ladeorte (aus ZHB_T02 Suche) */}
                    {secondaryLocations.length > 0 && (
                      <>
                        {primaryLocations.filter(location =>
                          !locationSearchTerm ||
                          locationSearchTerm.length < 2 ||
                          location.displayText.toLowerCase().includes(locationSearchTerm.toLowerCase())
                        ).length > 0 && (
                            <div style={{ height: "1px", backgroundColor: "#e5e7eb", margin: "4px 0" }}></div>
                          )}
                        <div style={{
                          padding: "8px 12px",
                          fontSize: "11px",
                          fontWeight: 600,
                          color: "#6b7280",
                          textTransform: "uppercase",
                          letterSpacing: "0.4px",
                          backgroundColor: "#f9fafb",
                          borderBottom: "1px solid #e5e7eb"
                        }}>
                          Weitere Ladeorte
                        </div>
                        {secondaryLocations.map((location) => (
                          <div
                            key={location.LADEORTNR}
                            style={{
                              padding: "10px 12px",
                              cursor: "pointer",
                              borderBottom: "1px solid #f3f4f6"
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f3f4f6")}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                            onClick={() => {
                              setLoadingLocation(location.displayText);
                              setLocationSearchTerm("");
                              setShowLocationDropdown(false);
                            }}
                          >
                            <div style={{ fontSize: "13px", color: "#1e293b" }}>
                              {location.displayText}
                            </div>
                          </div>
                        ))}
                      </>
                    )}

                    {/* Keine Ergebnisse */}
                    {primaryLocations.filter(location =>
                      !locationSearchTerm ||
                      locationSearchTerm.length < 2 ||
                      location.displayText.toLowerCase().includes(locationSearchTerm.toLowerCase())
                    ).length === 0 && secondaryLocations.length === 0 && (
                        <div style={{ padding: "12px", textAlign: "center", color: "#6b7280", fontSize: "13px" }}>
                          {locationSearchTerm.length < 2
                            ? "Mindestens 2 Zeichen eingeben..."
                            : "Keine Ladeorte gefunden"}
                        </div>
                      )}
                  </>
                )}
              </div>
            )}
          </label>
          {wasteError ? <div className="step-warning">{wasteError}</div> : null}
          <div className="step-actions">
            <button className="secondary" type="button" onClick={handleCheckinBack}>
              {t("actions.back")}
            </button>
            <button className="primary" type="submit">
              {t("actions.continue")}
            </button>
          </div>
        </form>
      );
    }

    if (checkinStep === "weightWaiting") {
      return (
        <div className="waiting-indicator">
          <div className="waiting-spinner" aria-hidden="true" />
          <h2>{t("checkin.weight.waitingTitle")}</h2>
          <p>{t("checkin.weight.waitingMessage")}</p>
          <div className="step-actions">
            <button className="secondary" type="button" onClick={handleCheckinBack}>
              {t("actions.back")}
            </button>
          </div>
        </div>
      );
    }

    if (checkinStep === "weightResult") {
      const statusPhase = checkinWeightConfirmation.phase;
      const statusText =
        statusPhase === "creating" || statusPhase === "pending"
          ? t("status.weightConfirmationAwaitingYard")
          : statusPhase === "confirmed"
            ? t("status.weightConfirmationApproved")
            : statusPhase === "error"
              ? checkinWeightConfirmation.error ?? t("status.weightConfirmationError")
              : t("weight.followUp");
      const isConfirmDisabled = statusPhase !== "confirmed";
      const isWeightVisible = statusPhase === "confirmed";
      const displayedWeight = isWeightVisible && checkinWeight != null ? checkinWeight : null;
      const formattedWeight = formatWeight(selectedLanguage, displayedWeight);
      const weightValueClassName = isWeightVisible ? "weight-value" : "weight-value weight-value--hidden";

      return (
        <div className="step-content">
          <div>
            <h2 className="step-title">{t("checkin.weight.resultTitle")}</h2>
            <p className="step-subtitle">{t("checkin.weight.resultSubtitle")}</p>
          </div>
          <div className="weight-result">
            <span className={weightValueClassName}>
              {isWeightVisible ? `${formattedWeight} ${t("weight.unit")}` : formattedWeight}
            </span>
            {!isWeightVisible ? (
              <p className="weight-placeholder-hint">{t("status.weightConfirmationHidden")}</p>
            ) : null}
            <div className={`weight-status weight-status-${statusPhase}`} role="status" aria-live="polite">
              <p className="weight-status-message">{statusText}</p>
              {statusPhase === "error" ? (
                <button className="secondary" type="button" onClick={retryCheckinWeightConfirmation}>
                  {t("actions.retry")}
                </button>
              ) : null}
            </div>
          </div>
          <div className="step-actions">
            <button className="secondary" type="button" onClick={handleCheckinBack}>
              {t("actions.back")}
            </button>
            <button className="primary" type="button" onClick={confirmCheckinWeight} disabled={isConfirmDisabled}>
              {t("actions.confirm")}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="success-card">
        <h2>{t("checkin.success.title")}</h2>
        <p>{t("checkin.success.subtitle")}</p>
        <div className="destination-tag">
          <span aria-hidden="true">📍</span>
          <span>{checkinTransport?.destinationLocation ?? t("weight.followUp")}</span>
        </div>
        {renderAutoResetNotice()}
        <div className="step-actions">
          <button className="primary" type="button" onClick={resetFlows}>
            {t("app.backToStart")}
          </button>
        </div>
      </div>
    );
  };

  const renderCheckoutContent = () => {
    if (checkoutStep === "batchNumber") {
      return (
        <form className="step-form" onSubmit={handleCheckoutBatchSubmit}>
          <div>
            <h2 className="step-title">{t("checkout.batch.title")}</h2>
            <p className="step-subtitle">{t("checkout.batch.subtitle")}</p>
          </div>
          <label>
            {t("checkout.batch.title")}
            <input
              value={checkoutBatchNumber}
              onChange={(event) => setCheckoutBatchNumber(event.target.value)}
              placeholder={t("checkout.batch.title")}
              disabled={loadingTransport}
            />
          </label>
          {checkoutBatchError ? <div className="step-warning">{checkoutBatchError}</div> : null}
          <div className="step-actions">
            <button className="secondary" type="button" onClick={goToModeSelection}>
              {t("actions.back")}
            </button>
            <button className="primary" type="submit" disabled={loadingTransport}>
              {loadingTransport ? t("actions.confirm") : t("actions.continue")}
            </button>
          </div>
        </form>
      );
    }

    if (checkoutStep === "licensePlate") {
      const hasSingleBatch = checkoutAvailableBatches.length === 1;
      const hasMultipleBatches = checkoutAvailableBatches.length > 1;

      return (
        <form className="step-form" onSubmit={handleCheckoutLicenseSubmit}>
          <div>
            <h2 className="step-title">{t("checkout.license.title")}</h2>
            <p className="step-subtitle">
              {hasMultipleBatches
                ? "Mehrere Transportchargen gefunden. Bitte wählen Sie das Kennzeichen aus."
                : t("checkout.license.subtitle")}
            </p>
          </div>

          {hasSingleBatch ? (
            // Einzelne Transportcharge: Ausgegraut und schreibgeschützt
            <label>
              {t("checkin.license.placeholder")}
              <input
                value={checkoutLicensePlate}
                placeholder={t("checkin.license.placeholder")}
                disabled
                style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed" }}
              />
            </label>
          ) : hasMultipleBatches ? (
            // Mehrere Transportchargen: Combobox zur Auswahl
            <label>
              {t("checkin.license.placeholder")}
              <select
                value={checkoutLicensePlate}
                onChange={(event) => {
                  const selectedLicensePlate = event.target.value;
                  setCheckoutLicensePlate(selectedLicensePlate);

                  // Lade die entsprechende Transportcharge
                  const selectedBatch = checkoutAvailableBatches.find(
                    batch => batch.truckLicensePlate === selectedLicensePlate
                  );

                  if (selectedBatch) {
                    setCheckoutTransport(selectedBatch);
                  }
                }}
                style={{ padding: "12px", fontSize: "16px" }}
              >
                <option value="">-- Kennzeichen auswählen --</option>
                {checkoutAvailableBatches.map((batch) => {
                  const batchSuffix = batch.transportBatchNumber.slice(-3);
                  const displayText = batch.truckLicensePlate
                    ? `${batch.truckLicensePlate} (${batchSuffix})`
                    : `Kein Kennzeichen (${batchSuffix})`;

                  return (
                    <option key={batch.id} value={batch.truckLicensePlate ?? ""}>
                      {displayText}
                    </option>
                  );
                })}
              </select>
            </label>
          ) : (
            // Fallback: Manuelle Eingabe (sollte nicht vorkommen, da mindestens 1 Batch existiert)
            <label>
              {t("checkin.license.placeholder")}
              <input
                value={checkoutLicensePlate}
                onChange={(event) => {
                  // Nur Buchstaben und Zahlen erlauben (keine Sonderzeichen, keine Leerzeichen, keine Bindestriche)
                  const sanitized = event.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
                  setCheckoutLicensePlate(sanitized);
                }}
                placeholder={t("checkin.license.placeholder")}
              />
            </label>
          )}

          {checkoutLicenseError ? <div className="step-warning">{checkoutLicenseError}</div> : null}
          <div className="step-actions">
            <button className="secondary" type="button" onClick={handleCheckoutBack}>
              {t("actions.back")}
            </button>
            <button className="primary" type="submit">
              {t("actions.continue")}
            </button>
          </div>
        </form>
      );
    }

    if (checkoutStep === "weightWaiting") {
      return (
        <div className="waiting-indicator">
          <div className="waiting-spinner" aria-hidden="true" />
          <h2>{t("checkout.weight.waitingTitle")}</h2>
          <p>{t("checkout.weight.waitingMessage")}</p>
          <div className="step-actions">
            <button className="secondary" type="button" onClick={handleCheckoutBack}>
              {t("actions.back")}
            </button>
          </div>
        </div>
      );
    }

    if (checkoutStep === "weightResult") {
      const statusPhase = checkoutWeightConfirmation.phase;
      const statusText =
        statusPhase === "creating" || statusPhase === "pending"
          ? t("status.weightConfirmationAwaitingYard")
          : statusPhase === "confirmed"
            ? t("status.weightConfirmationApproved")
            : statusPhase === "error"
              ? checkoutWeightConfirmation.error ?? t("status.weightConfirmationError")
              : t("weight.followUp");
      const isConfirmDisabled = statusPhase !== "confirmed";
      const isWeightVisible = statusPhase === "confirmed";
      const displayedWeight = isWeightVisible && checkoutWeight != null ? checkoutWeight : null;
      const formattedWeight = formatWeight(selectedLanguage, displayedWeight);
      const weightValueClassName = isWeightVisible ? "weight-value" : "weight-value weight-value--hidden";

      return (
        <div className="step-content">
          <div>
            <h2 className="step-title">{t("checkout.weight.resultTitle")}</h2>
            <p className="step-subtitle">{t("checkin.weight.resultSubtitle")}</p>
          </div>
          <div className="weight-result">
            <span className={weightValueClassName}>
              {isWeightVisible ? `${formattedWeight} ${t("weight.unit")}` : formattedWeight}
            </span>
            {!isWeightVisible ? (
              <p className="weight-placeholder-hint">{t("status.weightConfirmationHidden")}</p>
            ) : null}
            <div className={`weight-status weight-status-${statusPhase}`} role="status" aria-live="polite">
              <p className="weight-status-message">{statusText}</p>
              {statusPhase === "error" ? (
                <button className="secondary" type="button" onClick={retryCheckoutWeightConfirmation}>
                  {t("actions.retry")}
                </button>
              ) : null}
            </div>
          </div>
          <div className="step-actions">
            <button className="secondary" type="button" onClick={handleCheckoutBack}>
              {t("actions.back")}
            </button>
            <button className="primary" type="button" onClick={confirmCheckoutWeight} disabled={isConfirmDisabled}>
              {t("actions.confirm")}
            </button>
          </div>
        </div>
      );
    }

    if (checkoutStep === "signature") {
      const formattedWeight = formatWeight(selectedLanguage, checkoutWeight);

      return (
        <div className="step-content">
          <div>
            <h2 className="step-title">{t("checkout.signature.title")}</h2>
            <p className="step-subtitle">{t("checkout.signature.subtitle")}</p>
          </div>
          {checkoutWeight != null && (
            <div style={{
              textAlign: "center",
              marginBottom: "16px",
              padding: "12px",
              backgroundColor: "#f3f4f6",
              borderRadius: "8px"
            }}>
              <div style={{
                fontSize: "14px",
                color: "#6b7280",
                marginBottom: "4px"
              }}>
                {t("checkout.weight.resultTitle")}
              </div>
              <div style={{
                fontSize: "24px",
                fontWeight: "600",
                color: "#1e293b"
              }}>
                {formattedWeight} {t("weight.unit")}
              </div>
            </div>
          )}
          <canvas
            ref={signatureCanvasRef}
            className="signature-pad"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerLeave={() => {
              isDrawingRef.current = false;
            }}
          />
          <div className="signature-actions">
            <button className="secondary" type="button" onClick={clearSignature}>
              {t("checkout.signature.clear")}
            </button>
          </div>
          {signatureError ? <div className="step-warning">{signatureError}</div> : null}
          <div className="step-actions">
            <button className="secondary" type="button" onClick={handleCheckoutBack}>
              {t("actions.back")}
            </button>
            <button className="primary" type="button" onClick={completeCheckoutSignature}>
              {t("actions.finish")}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="step-content">
        <div className="success-card">
          <h2>{t("checkout.success.title")}</h2>
          <p>{t("checkout.success.subtitle")}</p>
        </div>
        <div className="step-info">
          <h3 className="step-title">{t("checkout.receipt.title")}</h3>
          <p className="step-subtitle">{t("checkout.receipt.subtitle")}</p>
        </div>
        {renderAutoResetNotice()}
        <div className="step-actions">
          <button className="primary" type="button" onClick={resetFlows}>
            {t("app.backToStart")}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="checkflow-root">
      <main className="checkflow-shell" aria-live="polite">
        <header className="checkflow-header">
          <h1>
            {stage === "checkin" ? t("app.titleCheckin")
              : stage === "checkout" ? t("app.titleCheckout")
                : t("app.title")}
          </h1>
          <div className="language-switcher">
            <button
              type="button"
              className="language-toggle"
              onClick={() => setShowLanguageMenu(!showLanguageMenu)}
              aria-label="Change language"
            >
              <span className={`fi fi-${SUPPORTED_LANGUAGES.find(l => l.key === selectedLanguage)?.flagCode}`} />
            </button>
            {showLanguageMenu && (
              <div className="language-menu">
                {SUPPORTED_LANGUAGES.map((language) => (
                  <button
                    key={language.key}
                    type="button"
                    className={`language-menu-item${selectedLanguage === language.key ? " active" : ""}`}
                    onClick={() => handleLanguageSelect(language.key)}
                  >
                    <span className={`fi fi-${language.flagCode}`} aria-hidden="true" />
                    <span>{language.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </header>
        {stage === "welcome" ? renderWelcomeScreen()
          : stage === "language" ? renderLanguageSelection()
            : stage === "mode" ? renderModeSelection()
              : stage === "redirectToGuard" ? renderRedirectToGuard()
                : stage === "checkin" ? renderCheckinContent()
                  : renderCheckoutContent()}
      </main>
    </div>
  );
}

export default CheckinCheckoutApp;
